import React, { useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Box } from "@material-ui/core";
import clsx from "clsx";
import {
  RewardsAccount,
  ConvertRewardsToUsdSuccess,
  RewardsAccountMinimumRequirementState,
  FiatPrice,
} from "redmond";
import {
  RewardsCheckoutWorkflowRow,
  RewardsCheckoutWorkflow,
  SplitPay,
  useDeviceTypes,
  NotificationBanner,
  BannerSeverity,
  Icon,
  IconName,
} from "halifax";
import { isCorpTenant } from "@capone/common";
import { RewardsSelectionConnectorProps } from "./container";
import * as textConstants from "./textConstants";
import { convertRewardsAmountToUsd } from "../../../../api/v0/rewards/convertRewardsAmountToUsd";
import { DO_NOT_APPLY_REWARDS_KEY } from "../../reducer";
import "./styles.scss";
import { convertUsdToRewards } from "../../../../api/v0/rewards/convertUsdToRewards";
import { config } from "../../../../api/config";
import { resolveSplitPayRowAmounts, resolveCappedCustomAccountAllocation } from "../../reducer/selectors/rewardsAllocation";
import {
  useExperiments,
  getExperimentVariant,
  CREDIT_OFFER_STACKING_V1,
  COMBINED_REWARDS_CHECKOUT,
  AVAILABLE,
} from "../../../../context/experiments";

export interface RewardsSelectionProps
  extends RewardsSelectionConnectorProps,
    RouteComponentProps {
  disabled?: boolean;
  rewardsTitleId: string;
  rewardsSubtitleId: string;
}

export const RewardsSelection = (props: RewardsSelectionProps) => {
  const {
    rewardsAccounts,
    selectedRewardsPaymentAccountId,
    rewardsPaymentInFiatCurrency,
    rewardsPaymentInRewardsCurrency,
    isCreditCardPaymentRequired,
    setSelectedRewardsAccountId,
    setRewardsPaymentAmount,
    setRewardsAccountCustomAmount,
    fetchProductToEarn,
    rewardsAccountMinimumRequirementState,
    disabled,
    total,
    totalCreditCardPaymentRequired,
    setPaymentMethod,
    setEarnValue,
    rewardsTitleId,
    rewardsSubtitleId,
    setQuotedRewardsTotal,
    setRewardsConversionFailed,
    offerToApply,
    creditToApply,
    userSelectedRewardsManually,
    setUserSelectedRewardsManually,
    selectedRewardsAccountReferenceIds,
    selectedRewardsAccounts,
    toggleRewardsAccount,
    clearSelectedRewardsAccounts,
    isMultiRewardsBalanceFullyCovered,
    rewardsAllocations,
  } = props;

  const { matchesMobile } = useDeviceTypes();

  const expState = useExperiments();
  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1
  );
  const isCreditAndOfferStackingExperimentV1 = useMemo(
    () => creditAndOfferStackingExperimentV1 === AVAILABLE,
    [creditAndOfferStackingExperimentV1]
  );

  const combinedRewardsCheckoutExperiment = getExperimentVariant(
    expState.experiments,
    COMBINED_REWARDS_CHECKOUT
  );
  const isMultiRewardsEligible = useMemo(
    () =>
      combinedRewardsCheckoutExperiment === AVAILABLE &&
      !isCorpTenant(config.TENANT),
    [combinedRewardsCheckoutExperiment]
  );

  React.useEffect(() => {
    if (!totalCreditCardPaymentRequired) {
      setPaymentMethod({ paymentMethod: undefined });
      setEarnValue(0);
    } else {
      // only fetch product to earn if credit card payment is required
      fetchProductToEarn();
    }
  }, [totalCreditCardPaymentRequired, fetchProductToEarn]);

  const handleSwitchAccount = async (account: RewardsAccount | null) => {
    if (!account) {
      setRewardsPaymentAmount(DO_NOT_APPLY_REWARDS_KEY, null, null, total);
      return;
    }

    const { rewardsBalance, rewardsCashEquivalent, accountReferenceId } =
      account;

    // If the account is not the same as the existing selected account.
    if (accountReferenceId !== selectedRewardsPaymentAccountId) {
      // TODO: seats + flight not calculated correctly for tiered account so call BE for accurate pricing
      if (account.isTiered && total) {
        const rewardsTotal = await convertUsdToRewards({
          amount: total?.fiat.value || 0,
          accountReferenceId: account.accountReferenceId,
        });
        total.accountSpecific[account.accountReferenceId] = rewardsTotal;
        setQuotedRewardsTotal(rewardsTotal);
      } else {
        setQuotedRewardsTotal(null);
      }
      setRewardsPaymentAmount(
        account.accountReferenceId,
        rewardsBalance,
        rewardsCashEquivalent,
        total,
      );
    }
  };

  React.useEffect(() => {
    if (rewardsAccounts.length > 0 && !userSelectedRewardsManually) {
      const everyAccountIneligible = rewardsAccounts.every(
        (account) => !(account.allowRewardsRedemption ?? true)
      );

      if (disabled || everyAccountIneligible) {
        setTimeout(() => {
          handleSwitchAccount(null);
          setSelectedRewardsAccountId(DO_NOT_APPLY_REWARDS_KEY);
        });
      }
    }
  }, [
    total,
    rewardsAccounts,
    userSelectedRewardsManually,
    disabled,
    selectedRewardsPaymentAccountId,
  ]);

  React.useEffect(() => {
    if (
      rewardsAccounts.length > 0 &&
      rewardsAccountMinimumRequirementState ===
        RewardsAccountMinimumRequirementState.ALL_ACCOUNT_DO_NOT_MEET_MINIMUM_REQUIREMENT &&
      !userSelectedRewardsManually
    ) {
      setTimeout(() => {
        handleSwitchAccount(null);
        setSelectedRewardsAccountId(DO_NOT_APPLY_REWARDS_KEY);
      });
    }
  }, [rewardsAccounts, rewardsAccountMinimumRequirementState, userSelectedRewardsManually]);

  const handleSetCustomRewardsAmount = async (
    customRewardsValue: number,
    account: RewardsAccount
  ) => {
    try {
      const fiatPrice = await convertRewardsAmountToUsd({
        rewardsAmount: customRewardsValue,
        accountReferenceId: account.accountReferenceId,
      });

      const convertedPrice = (fiatPrice as ConvertRewardsToUsdSuccess).value;

      if (!convertedPrice) {
        throw new Error("Could not fetch split pay conversion.");
      }

      // TODO: seats + flight not calculated correctly for tiered account so call BE for accurate pricing
      if (account.isTiered && total) {
        const rewardsTotal = await convertUsdToRewards({
          amount: total?.fiat.value || 0,
          accountReferenceId: account.accountReferenceId,
        });
        total.accountSpecific[account.accountReferenceId] = rewardsTotal;
        setQuotedRewardsTotal(rewardsTotal);
      } else {
        setQuotedRewardsTotal(null);
      }

      setRewardsPaymentAmount(
        account.accountReferenceId,
        {
          value: customRewardsValue,
          currency: account.rewardsBalance.currency,
        },
        {
          value: convertedPrice,
          currencyCode: account.rewardsCashEquivalent.currencyCode,
          currencySymbol: account.rewardsCashEquivalent.currencySymbol,
        },
        total
      );
    } catch {
      // TODO: Figure out error handling with product, for now, set the rewards to the full account balance if we cannot convert.
      setRewardsConversionFailed();
      setRewardsPaymentAmount(
        account.accountReferenceId,
        account.rewardsBalance,
        account.rewardsCashEquivalent,
        total
      );
    }
  };

  const handleToggleAccount = (
    accountReferenceId: string | null,
    selected: boolean
  ) => {
    if (!accountReferenceId) {
      return;
    }
    setUserSelectedRewardsManually(true);
    if (accountReferenceId === DO_NOT_APPLY_REWARDS_KEY) {
      if (selected) {
        clearSelectedRewardsAccounts();
      }
      return;
    }
    toggleRewardsAccount(accountReferenceId, selected);
  };

  const getSelectedRewardsAccountEntry = (accountReferenceId: string) =>
    selectedRewardsAccounts.find(
      (selectedAccount) =>
        selectedAccount.accountReferenceId === accountReferenceId
    );

  const handleSetCustomRewardsAmountForAccount = async (
    customRewardsValue: number,
    account: RewardsAccount
  ) => {
    try {
      const fiatPrice = await convertRewardsAmountToUsd({
        rewardsAmount: customRewardsValue,
        accountReferenceId: account.accountReferenceId,
      });

      const convertedPrice = (fiatPrice as ConvertRewardsToUsdSuccess).value;

      if (!convertedPrice) {
        throw new Error("Could not fetch split pay conversion.");
      }

      // cap the entered amount so it can't exceed what's still needed once the other
      // selected accounts' allocations are taken into account, reusing the same
      // remaining-balance-in-selection-order math used to build the real payment request.
      const cappedAllocation = resolveCappedCustomAccountAllocation(
        selectedRewardsAccounts,
        rewardsAccounts,
        total,
        account.accountReferenceId,
        convertedPrice
      );

      setRewardsAccountCustomAmount(
        account.accountReferenceId,
        cappedAllocation
          ? {
              value: cappedAllocation.rewards.value,
              currency: cappedAllocation.rewards.currency,
            }
          : {
              value: customRewardsValue,
              currency: account.rewardsBalance.currency,
            },
        cappedAllocation
          ? cappedAllocation.fiat
          : {
              value: convertedPrice,
              currencyCode: account.rewardsCashEquivalent.currencyCode,
              currencySymbol: account.rewardsCashEquivalent.currencySymbol,
            },
        total
      );
    } catch {
      setRewardsConversionFailed();
      setRewardsAccountCustomAmount(
        account.accountReferenceId,
        null,
        null,
        total
      );
    }
  };

  const renderRewardAccountRow = ({
    account,
    isSelectedAccount,
    isExpanded,
  }: {
    account?: RewardsAccount;
    isSelectedAccount: boolean;
    isExpanded?: boolean;
  }) => {
    const minRewardsAmountNotMatched =
      account && account.redemptionMin?.value > account.rewardsBalance.value;
    const emptyRewards = account && account.rewardsBalance?.value <= 0;
    const unselectedAccountNotNeeded =
      isMultiRewardsEligible &&
      !isSelectedAccount &&
      !!account &&
      isMultiRewardsBalanceFullyCovered;

    return (
      <RewardsCheckoutWorkflowRow
        account={account}
        titles={{
          rewardsApplyText: textConstants.SPLIT_PAY_REWARDS_APPLY_TEXT,
          doNotApplyRewardsText: textConstants.SPLIT_PAY_DO_NOT_APPLY_TEXT,
        }}
        aria-expanded={isExpanded}
        setSelectedAccountReferenceId={(accountId) => {
          setUserSelectedRewardsManually(true);
          setSelectedRewardsAccountId(accountId);
        }}
        doNotApplyRewardsKey={DO_NOT_APPLY_REWARDS_KEY}
        onSwitchAccount={handleSwitchAccount}
        isSelectedAccount={isSelectedAccount}
        isMobile={matchesMobile}
        disabled={
          account
            ? disabled ||
              minRewardsAmountNotMatched ||
              emptyRewards ||
              unselectedAccountNotNeeded
            : disabled
        }
        multiSelect={isMultiRewardsEligible}
        onToggleAccount={handleToggleAccount}
      />
    );
  };


  const updatedRewardsPaymentInFiatCurrency: FiatPrice | null = rewardsPaymentInFiatCurrency;
  const updatedCustomAmount: number | undefined = rewardsPaymentInRewardsCurrency?.value;

  const renderSplitPay = (account: RewardsAccount) => {
    const selectedAccountEntry = isMultiRewardsEligible
      ? getSelectedRewardsAccountEntry(account.accountReferenceId)
      : undefined;

    const allocationForAccount = isMultiRewardsEligible
      ? rewardsAllocations.find(
          (allocation) =>
            allocation.accountReferenceId === account.accountReferenceId
        )
      : undefined;

    const { customAmount: multiCustomAmount, fiat: multiFiatCurrency } =
      resolveSplitPayRowAmounts(selectedAccountEntry, allocationForAccount);

    const customAmount = isMultiRewardsEligible
      ? multiCustomAmount
      : updatedCustomAmount;
    const rewardsPaymentInFiatCurrencyForAccount = isMultiRewardsEligible
      ? multiFiatCurrency
      : updatedRewardsPaymentInFiatCurrency;

    return (
      <SplitPay
        account={account}
        splitPayCoversTotal={
          isMultiRewardsEligible ? false : !isCreditCardPaymentRequired
        }
        customAmount={customAmount}
        setCustomAmount={(value) =>
          isMultiRewardsEligible
            ? handleSetCustomRewardsAmountForAccount(value, account)
            : handleSetCustomRewardsAmount(value, account)
        }
        rewardsPaymentInFiatCurrency={rewardsPaymentInFiatCurrencyForAccount}
        titles={{
          applyText: textConstants.SPLIT_PAY_APPLY_TEXT,
          editButtonText: textConstants.SPLIT_PAY_EDIT_TEXT,
          saveButtonText: textConstants.SPLIT_PAY_SAVE_TEXT,
          cancelButtonText: textConstants.SPLIT_PAY_CANCEL_TEXT,
          customAmountTooHighError:
            textConstants.SPLIT_PAY_CUSTOM_AMOUNT_TOO_HIGH,
          customAmountGenericError:
            textConstants.SPLIT_PAY_CUSTOM_AMOUNT_GENERIC_ERROR,
          saveNotification: textConstants.SPLIT_PAY_SAVE_NOTIFICATION,
          coversTotalCostNotification: isCreditAndOfferStackingExperimentV1
            ? textConstants.getSplitPayCoverageNotification(
                !!offerToApply,
                !!creditToApply
              )
            : textConstants.SPLIT_PAY_COVERS_TOTAL_COST_NOTIFICATION,
        }}
        isMobile={matchesMobile}
        disabled={disabled}
      />
    );
  };

  return (
    <RewardsCheckoutWorkflow
      rewardsAccounts={rewardsAccounts}
      renderRewardAccountRow={renderRewardAccountRow}
      renderSplitPay={renderSplitPay}
      selectedAccountReferenceId={selectedRewardsPaymentAccountId ?? null}
      doNotApplyRewardsKey={DO_NOT_APPLY_REWARDS_KEY}
      isMobile={matchesMobile}
      disabled={disabled}
      shouldDisableAccount={() => false}
      multiSelect={isMultiRewardsEligible}
      selectedAccountReferenceIds={selectedRewardsAccountReferenceIds}
      userSelectedRewardsManually={userSelectedRewardsManually}
      renderBeforeOrDivider={() =>
        isMultiRewardsEligible && isMultiRewardsBalanceFullyCovered ? (
          <Box
            className="multi-rewards-checkout-notification-section"
            key="covers-total-cost-banner"
          >
            <NotificationBanner
              icon={<Icon name={IconName.CheckCircle} />}
              label={
                isCreditAndOfferStackingExperimentV1
                  ? textConstants.getSplitPayCoverageNotification(
                      !!offerToApply,
                      !!creditToApply
                    )
                  : textConstants.SPLIT_PAY_COVERS_TOTAL_COST_NOTIFICATION
              }
              severity={BannerSeverity.IMPROVEMENT}
              className={clsx("notification-container", "notification")}
            />
          </Box>
        ) : null
      }
      {...{ rewardsTitleId, rewardsSubtitleId }}
    />
  );
};
